/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365 from '@msdyn365-commerce/core';
import { IHeaderViewProps, Logo } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { IHeaderData } from '@msdyn365-commerce-modules/header/dist/types/modules/header/header.data';
import { getPreferredStoreAsync } from '../../../custom-actions/DataActionExtension.g';
import CartIconComponent from './components/carticon.component';
import { ICartState } from '@msdyn365-commerce/global-state';
import { reaction } from 'mobx';
import { IPreferredStoreEntity } from '../../../custom-actions/DataServiceEntities.g';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface IStoreDataCustom {
    Latitude?: number;
    Longitude?: number;
    StoreId?: string;
    StoreName?: string;
}

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    private readonly menuNode: React.RefObject<HTMLDivElement>;
    private readonly telemetryContent: ITelemetryContent;
    private cart: ICartState | undefined;

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
            reaction(
                () => {
                    this.props.data.cart.result;
                },
                () => {
                    this.cart = this.props.data.cart.result;
                }
            );
        }
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            id,
            typeName,
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider,
            MobileMenuContainer,
            MobileMenuBodyContainer,
            MobileMenuLinksContainer,
            context,
            resources
        } = this.props;
        const { cartLabel, cartQtyLabel } = resources;

        const preferredStoreSlot = this._getSlot('storeSelector');
        const cartIconSlot = this._getSlot('cartIcon');
        const user = context?.request?.user;

        return (
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        <div className='desktop-navicon-container' ref={this.menuNode}>
                            {this._renderReactFragment(this.props.menuBar)}
                        </div>
                        <div className='mobile-navicon-container'>{this.props.navIcon}</div>
                        {this.props.logo}
                        {this._renderMobileLogo()}
                        {this._renderReactFragment(this.props.search)}
                        {/* {this.props.preferredStore} */}
                        {ArrayExtensions.hasElements(preferredStoreSlot) && <HeaderPreferredStore {...this.props} />}
                        {this.props.currentDistributor}
                        {this._renderAccountBlock(this.props)}
                        <Node {...Divider} />
                        {user.isAuthenticated && (
                            <>
                                {ArrayExtensions.hasElements(cartIconSlot) ? (
                                    cartIconSlot[0]
                                ) : (
                                    <CartIconComponent
                                        cartLabel={cartLabel}
                                        cartQtyLabel={cartQtyLabel}
                                        context={context}
                                        id={id}
                                        typeName={typeName}
                                        telemetryContent={this.telemetryContent}
                                        data={{ cart: this.cart }}
                                    />
                                )}
                            </>
                        )}
                        {this._renderReactFragment(this.props.siteOptions)}
                    </Node>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {this.props.MobileMenuHeader}
                            {this._renderReactFragment(this.props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {this.props.accountLinks ? this.props.accountLinks.map(link => link) : false}
                                {this.props.siteOptions}
                                {this.props.wishListIconMobile}
                                {this.props.signInLink}
                                {this.props.signOutLink}
                            </Node>
                        </Node>
                    </Node>
                </Node>
            </Module>
        );
    }

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderMobileLogo(): JSX.Element | null {
        const { config, context, typeName } = this.props;
        return (
            <Logo
                {...{
                    link: config.logoLink,
                    image: config.mobileLogoImage ?? {},
                    className: 'ms-header__mobile-logo',
                    gridSettings: context.request.gridSettings,
                    requestContext: context.request,
                    typeName
                }}
            />
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderAccountBlock = (props: IHeaderViewProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signInLink
        } = props;
        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(AccountInfoDropdownParentContainer.className, 'account-desktop');
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>{this._renderAccountDropdownLinks(props)}</Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    };

    private readonly _renderAccountDropdownLinks = (props: IHeaderViewProps): JSX.Element | null => {
        const { isOBORequest, switchCustomerLink, accountLinks, signOutLink } = props;
        if (isOBORequest) {
            return (
                <>
                    {switchCustomerLink}
                    {signOutLink}
                    {accountLinks ? <Node className='ms-profile-button-splitter' /> : false}
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                </>
            );
        } else {
            return (
                <>
                    {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                    {signOutLink}
                </>
            );
        }
    };

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };

    /**
     * Get the slot.
     * @param slotName - Name of the slot.
     * @returns The slot.
     */
    private _getSlot(slotName: 'storeSelector' | 'cartIcon'): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }
}

interface IHeaderViewState {
    isPreferredStoreFetched: boolean;
}
export class HeaderPreferredStore extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>, IHeaderViewState> {
    private readonly modalCloseButtonRef: React.RefObject<HTMLButtonElement> = React.createRef();

    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
        this.state = {
            isPreferredStoreFetched: false
        };
    }

    private setSelectedStore = async (store: IPreferredStoreEntity) => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: { storeSelectorStateManager }
        } = this.props;
        if (!storeSelectorStateManager?.result) {
            console.warn('storeSelectorStateManager is not yet available.');
            return;
        }
        const storeSelectorStateManagerInstance = storeSelectorStateManager.result;
        if (!storeSelectorStateManagerInstance.setPreferredStoreLocation) {
            console.warn('setPreferredStoreLocation is undefined.');
            return;
        }
        await storeSelectorStateManagerInstance.setPreferredStoreLocation(
            store.StoreId,
            store.StoreName,
            cookies.isConsentGiven(),
            store.Latitude,
            store.Longitude
        );
        this.setState({ isPreferredStoreFetched: true });
        if (MsDyn365.isBrowser && typeof window !== 'undefined') {
            localStorage.setItem(
                'logged_in_user_with_store',
                JSON.stringify({
                    name: this.props?.context?.request?.user?.name ?? '',
                    email: this.props?.context?.request?.user?.emailAddress ?? '',
                    customerAccountNumber: this.props?.context?.request?.user?.customerAccountNumber ?? '',
                    token: this.props?.context?.request?.user?.token ?? '',
                    store: { ...store }
                })
            );
        }
    };

    private getSelectedStore = async () => {
        if (!this.props.context?.request?.user?.customerAccountNumber) {
            return;
        }
        const userWithStore = JSON.parse(localStorage.getItem('logged_in_user_with_store') || '{}');
        const preferredStore = JSON.parse(localStorage.getItem('_msdyn365__preferredStore_') || '{}');
        const user = this.props?.context?.request?.user;
        const isUserSignedIn = user?.isSignedIn;
        const isPreferredStoreMissing = !preferredStore?.StoreId;
        const isUserMismatch = user?.emailAddress !== userWithStore?.email || user?.token !== userWithStore?.token;

        try {
            const res = await getPreferredStoreAsync(
                { callerContext: this.props.context.actionContext },
                this.props.context.request.user.customerAccountNumber
            );

            if (res?.StoreId) {
                if (
                    isUserSignedIn &&
                    (isPreferredStoreMissing ||
                        isUserMismatch ||
                        preferredStore.StoreId !== res.StoreId ||
                        userWithStore.store.StoreId !== preferredStore.StoreId)
                ) {
                    this.setSelectedStore(res);
                }
            } else {
                if (MsDyn365.isBrowser && typeof window !== 'undefined') {
                    localStorage.removeItem('_msdyn365__preferredStore_');
                }
                this.modalCloseButtonRef?.current?.click();
            }
        } catch (err) {
            console.log(err);
            if (MsDyn365.isBrowser && typeof window !== 'undefined') {
                localStorage.removeItem('_msdyn365__preferredStore_');
            }
            this.modalCloseButtonRef?.current?.click();
            this.setState({ isPreferredStoreFetched: true });
        }
    };

    public componentDidUpdate(): void {
        if (!MsDyn365.isBrowser) {
            console.error('Not in browser environment');
            return;
        }
        const currentStoreSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const isConsentGiven =
            this.props.context.request &&
            this.props.context.request.cookies &&
            this.props.context.request.cookies.isConsentGiven &&
            this.props.context.request.cookies.isConsentGiven();
        if (
            !this.state.isPreferredStoreFetched &&
            currentStoreSelectorStateManager?.setPreferredStoreLocation &&
            isConsentGiven &&
            this.props.context?.request?.user?.customerAccountNumber
        ) {
            this.getSelectedStore();
        }
    }
    /**
     * Handle click on the store selector.
     * @returns : Promise void.
     */
    public handleClick = () => {
        return this._findPreferredStoreOnClick(this.props);
    };

    public render(): JSX.Element | undefined {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            slots,
            resources
        } = this.props;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- slots can be undefined.
        if (ArrayExtensions.hasElements(slots?.storeSelector)) {
            const storeName =
                storeSelectorStateManager?.preferredStore && storeSelectorStateManager.preferredStore.StoreName
                    ? storeSelectorStateManager.preferredStore.StoreName
                    : resources.headerPreferredStoreText;

            return (
                <div className='ms-header__preferred-store-container'>
                    <button
                        className='ms-header__preferred-store-btn msc-btn'
                        color='secondary'
                        aria-label={resources.headerPreferredStoreAriaLabel}
                        onClick={this.handleClick}
                        ref={this.modalCloseButtonRef}
                    >
                        <span className='ms-header__preferred-store-text'>{storeName}</span>
                    </button>
                    {slots.storeSelector}
                </div>
            );
        }

        return undefined;
    }

    private async _findPreferredStoreOnClick(props: IHeaderProps<IHeaderData>): Promise<void> {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            modules
        } = props;
        if (!storeSelectorStateManager) {
            return;
        }
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && ArrayExtensions.hasElements(modules.storeSelector)) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        await storeSelectorStateManager
            .openDialog({
                id: storeSelectorId,
                showMapViewLink: true,
                parentElementRef: this.modalCloseButtonRef,
                onLocationSelected: orgUnitLocation => {
                    return Promise.resolve();
                }
            })
            .catch((error: Error) => {
                if (props.telemetry) {
                    props.telemetry.error(error.message);
                    props.telemetry.debug('Unable to set preferred store');
                }
            });
    }
}

export default HeaderView;
